import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEO = {
  de: {
    lang: 'de',
    title: 'Restroos – Provisionsfreies Bestellsystem für Restaurants | 0 % Provision',
    description:
      'Nehmen Sie Online-Bestellungen direkt entgegen – ohne Provision, ohne Mittelsmann. Echtzeit-Benachrichtigungen, automatischer Bondruck, €70/Monat. Jetzt 7 Tage kostenlos testen.',
    keywords:
      'Bestellsystem Restaurant, provisionsfreies Bestellsystem, Online Bestellsystem Restaurant, Restaurant Software, Bestellsystem ohne Provision, Restaurantsoftware Deutschland, eigenes Bestellsystem, direkte Bestellplattform',
    ogLocale: 'de_DE',
    ogLocaleAlt: 'en_GB',
    ogImageAlt: 'Restroos – provisionsfreies Online-Bestellsystem für Restaurants',
  },
  en: {
    lang: 'en',
    title: 'Restroos – Commission-Free Restaurant Ordering System | Zero Commission',
    description:
      'Take direct online orders with 0% commission — no delivery platform middleman, real-time notifications, auto-print receipts. 7-day free trial from €70/month.',
    keywords:
      'restaurant ordering system, commission-free ordering, restaurant management software, online ordering platform, zero commission restaurant, direct ordering system, restaurant POS alternative',
    ogLocale: 'en_GB',
    ogLocaleAlt: 'de_DE',
    ogImageAlt: 'Restroos – commission-free online ordering system for restaurants',
  },
} as const;

const SITE_URL = 'https://restroos.com';

const JSON_LD_DE = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Restroos',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/{search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Restroos',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.svg`,
    sameAs: ['https://instagram.com/restro_os_0'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Restroos',
    url: SITE_URL,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    description:
      'Provisionsfreies Online-Bestellsystem für Restaurants – direkte Bestellungen ohne Mittelsmann, 0 % Provision, Echtzeit-Benachrichtigungen und automatischer Bondruck.',
    inLanguage: ['de', 'en'],
    offers: {
      '@type': 'Offer',
      price: '70',
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '70',
        priceCurrency: 'EUR',
        referenceQuantity: { '@type': 'QuantitativeValue', value: '1', unitCode: 'MON' },
      },
    },
    publisher: { '@type': 'Organization', name: 'Restroos', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Was kostet Restroos?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Restroos kostet €70 pro Monat – mit einer 7-tägigen kostenlosen Testphase. Keine Provision pro Bestellung, keine versteckten Gebühren.',
        },
      },
      {
        '@type': 'Question',
        name: 'Ist Restroos günstiger als Drittanbieter-Lieferplattformen?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Ja. Drittanbieter-Plattformen nehmen bis zu 30 % Provision pro Bestellung. Restroos erhebt 0 % Provision – Sie zahlen nur €70/Monat und behalten 100 % Ihrer Einnahmen.',
        },
      },
      {
        '@type': 'Question',
        name: 'Wie viel Provision nimmt Restroos?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Restroos erhebt 0 % Provision auf Ihre Bestellungen. Sie zahlen ausschließlich die monatliche Abo-Gebühr von €70 sowie die üblichen Stripe-Zahlungsgebühren.',
        },
      },
      {
        '@type': 'Question',
        name: 'Wie schnell kann ich mein Bestellsystem einrichten?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'In weniger als 30 Minuten. Registrieren, Menü anlegen, Link teilen – fertig. Kein technisches Wissen erforderlich.',
        },
      },
    ],
  },
]);

const JSON_LD_EN = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Restroos',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/{search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Restroos',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.svg`,
    sameAs: ['https://instagram.com/restro_os_0'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Restroos',
    url: SITE_URL,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    description:
      'Commission-free restaurant ordering system — take direct orders with 0% commission, real-time notifications, and auto-print receipts. No delivery platform middleman.',
    inLanguage: ['de', 'en'],
    offers: {
      '@type': 'Offer',
      price: '70',
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '70',
        priceCurrency: 'EUR',
        referenceQuantity: { '@type': 'QuantitativeValue', value: '1', unitCode: 'MON' },
      },
    },
    publisher: { '@type': 'Organization', name: 'Restroos', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How much does Restroos cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Restroos costs €70 per month with a 7-day free trial. Zero commission per order, no hidden fees.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Restroos cheaper than third-party delivery platforms?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Third-party delivery platforms charge up to 30% commission per order. Restroos charges 0% commission — you pay a flat €70/month and keep 100% of your revenue.',
        },
      },
      {
        '@type': 'Question',
        name: 'What commission does Restroos charge?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Restroos charges 0% commission on your orders. You only pay the €70/month subscription and standard Stripe payment processing fees.',
        },
      },
      {
        '@type': 'Question',
        name: 'How quickly can I set up my ordering system?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Under 30 minutes. Register, add your menu, share your link — done. No technical knowledge needed.',
        },
      },
    ],
  },
]);

export function SeoHead() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('de') ? 'de' : 'en';
  const seo = SEO[locale];
  const jsonLd = locale === 'de' ? JSON_LD_DE : JSON_LD_EN;

  return (
    <Helmet>
      <html lang={seo.lang} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />

      {/* Open Graph */}
      <meta property="og:site_name" content="Restroos" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={SITE_URL} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.ogImageAlt} />
      <meta property="og:locale" content={seo.ogLocale} />
      <meta property="og:locale:alternate" content={seo.ogLocaleAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Restroos" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
      <meta name="twitter:image:alt" content={seo.ogImageAlt} />

      {/* hreflang */}
      <link rel="alternate" hrefLang="de" href={SITE_URL} />
      <link rel="alternate" hrefLang="en" href={SITE_URL} />
      <link rel="alternate" hrefLang="x-default" href={SITE_URL} />

      {/* Canonical */}
      <link rel="canonical" href={SITE_URL} />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">{jsonLd}</script>
    </Helmet>
  );
}
